import { useEffect, useRef } from "react";
import { api } from "@/lib/apiClient";

const POLL_MS = 15000;

/**
 * Keeps a transaction's comment thread live without a manual refresh — the admin, customer and
 * advocate case pages all showed a message from the other side only after the page was reloaded,
 * because `GET /transactions/{tid}/comments` was fetched once on mount and never again.
 *
 * Same shape as the admin queue's own live-update (backend/CLAUDE.md "The admin queue refreshes
 * itself"): paused while the tab is hidden, paused whenever the caller says so (e.g. a dialog that
 * shouldn't have data swapped out from under it), and never lets two polls overlap. Unlike the
 * queue there is no separate cheap "pulse" indirection — a single case's comment list is already
 * a small, indexed lookup, not the expensive full-table scan the queue's list is, so polling the
 * real endpoint on an interval is the right amount of machinery here.
 *
 */
export function useCommentsPoll(tid, onData, { paused = false } = {}) {
  const inFlight = useRef(false);
  useEffect(() => {
    if (!tid) return;
    let stopped = false;
    const tick = async () => {
      if (stopped || inFlight.current || document.hidden || paused) return;
      inFlight.current = true;
      try {
        const { data } = await api.get(`/transactions/${tid}/comments`);
        if (!stopped) onData(data);
      } catch {
        // A failed background poll isn't worth surfacing — the thread still shows the last good
        // data, and the next tick retries. Showing an error for this trains people to ignore them.
      } finally {
        inFlight.current = false;
      }
    };
    // No immediate tick: the page's own initial load already fetched comments once: polling only
    // needs to pick up from here.
    const id = setInterval(tick, POLL_MS);
    const onVisible = () => { if (!document.hidden) tick(); };
    document.addEventListener("visibilitychange", onVisible);
    return () => {
      stopped = true;
      clearInterval(id);
      document.removeEventListener("visibilitychange", onVisible);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tid, paused]);
}
